import { Suspense, lazy, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RouteTracker } from "@/components/RouteTracker";
import { Analytics } from "@vercel/analytics/react";
import { captureUTMParams, captureLandingPage } from '@/utils/revenueAttribution';

// Eagerly load the two most-visited pages
import Index from "@/pages/Index";
import Compare from "@/pages/Compare";

// Lazy-load everything else to keep initial bundle small
const FirmDetail = lazy(() => import("@/pages/FirmDetail"));
const About = lazy(() => import("@/pages/About"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Methodology = lazy(() => import("@/pages/Methodology"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Tools
const Tools = lazy(() => import("@/pages/Tools"));
const DrawdownSimulator = lazy(() => import("@/pages/DrawdownSimulator"));

// Programmatic SEO landing pages
const BestOf = lazy(() => import("@/pages/BestOf"));

// Category landing pages
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="w-8 h-8 rounded-full border-4 border-brand-blue border-t-transparent animate-spin" />
  </div>
);

const App = () => {
  // Capture UTM parameters and landing page on first load for revenue attribution
  useEffect(() => {
    captureUTMParams();
    captureLandingPage();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Core pages (eagerly loaded) */}
              <Route path="/" element={<Index />} />
              <Route path="/compare" element={<Compare />} />

              {/* Firm detail & info */}
              <Route path="/firms/:slug" element={<FirmDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/methodology" element={<Methodology />} />

              {/* Blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* Legal */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Tools */}
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/drawdown-simulator" element={<DrawdownSimulator />} />

              {/* Programmatic SEO landing pages */}
              <Route path="/best/:category" element={<BestOf />} />

              {/* Category landing pages */}
              <Route path="/category/:slug" element={<CategoryPage />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Analytics />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
